import React, { useCallback, useEffect, useState } from 'react';
import { HardHat, X, Plus } from 'lucide-react';
import SectionShell from './SectionShell';
import { jobBriefApi } from '../../../services/jobBriefApi';

/**
 * PPESection — chip-based multi-select from the canonical PPE catalog,
 * plus a free-text "custom item" input for anything the catalog misses.
 * The selected PPE list is stored on `draft.ppe` as an array of strings.
 */
export default function PPESection({ draft, onChange }) {
  const ppe = draft.ppe || [];
  const [catalog, setCatalog] = useState([]);
  const [customItem, setCustomItem] = useState('');

  useEffect(() => {
    jobBriefApi.getPpeCatalog().then((d) => setCatalog(d?.ppe || []));
  }, []);

  const toggle = useCallback((label) => {
    const next = ppe.includes(label) ? ppe.filter((p) => p !== label) : [...ppe, label];
    onChange({ ppe: next });
  }, [ppe, onChange]);

  const addCustom = useCallback(() => {
    const v = customItem.trim();
    if (!v || ppe.includes(v)) return;
    onChange({ ppe: [...ppe, v] });
    setCustomItem('');
  }, [customItem, ppe, onChange]);

  const remove = useCallback((label) => {
    onChange({ ppe: ppe.filter((p) => p !== label) });
  }, [ppe, onChange]);

  return (
    <SectionShell
      icon={HardHat}
      title="Personal Protective Equipment"
      subtitle="Pick every required PPE item. Add custom items for anything the catalog is missing."
      color="#67e8f9"
      badge={ppe.length ? `${ppe.length} selected` : 'Required'}
      testId="jbv2-section-ppe"
    >
      <div className="jbv2-chips" data-testid="jbv2-ppe-catalog-chips">
        {catalog.map((p) => {
          const on = ppe.includes(p.label);
          return (
            <button
              key={p.id}
              type="button"
              className={`jbv2-chip${on ? ' jbv2-chip--on' : ''}`}
              onClick={() => toggle(p.label)}
              data-testid={`jbv2-ppe-catalog-${p.id}`}
              aria-pressed={on}
            >
              {p.label}
            </button>
          );
        })}
      </div>

      {/* Selected list with individual remove for custom items */}
      {ppe.filter((p) => !catalog.some((c) => c.label === p)).length > 0 && (
        <>
          <div className="jbv2-field-label" style={{ marginTop: 4 }}>Custom items</div>
          <div className="jbv2-chips" data-testid="jbv2-ppe-custom-chips">
            {ppe.filter((p) => !catalog.some((c) => c.label === p)).map((p) => (
              <span key={p} className="jbv2-chip jbv2-chip--on" data-testid={`jbv2-ppe-custom-item-${p}`}>
                {p}
                <button
                  type="button"
                  onClick={() => remove(p)}
                  style={{ background: 'transparent', border: 'none', color: 'inherit', cursor: 'pointer', padding: 0, marginLeft: 4 }}
                  data-testid={`jbv2-ppe-custom-remove-${p}`}
                  aria-label={`Remove ${p}`}
                >
                  <X size={11} />
                </button>
              </span>
            ))}
          </div>
        </>
      )}

      <div className="jbv2-add-row" data-testid="jbv2-ppe-add-row">
        <input
          type="text"
          value={customItem}
          onChange={(e) => setCustomItem(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } }}
          placeholder="Custom PPE item"
          data-testid="jbv2-ppe-custom-input"
          aria-label="Custom PPE item"
        />
        <button
          type="button"
          onClick={addCustom}
          disabled={!customItem.trim()}
          data-testid="jbv2-ppe-custom-add"
        >
          <Plus size={13} /> Add
        </button>
      </div>
    </SectionShell>
  );
}
